import React, { useEffect, useRef, useState } from 'react';
import { closeStage, openStageWindow, setStageMode } from './lessonStage';
import { openVideoWindow } from '../youtubeVideo';
import { stageModeLabel } from './stageState';
import useStagePresence from './useStagePresence';
import './StageButton.css';

/** A touch-first device (an iPad or a phone): no window to drag anywhere. */
const isTouchDevice = () =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(pointer: coarse)').matches &&
	!window.matchMedia('(pointer: fine)').matches;

/**
 * The Second screen control (Lesson Dashboard pass 3 G1/G3/G4, 2026-09-28),
 * in the track strip. It says what the room's stage window is doing:
 * **On · Stage window · Lyrics** (or Video / Blank / Ready), or **Off**.
 * Tapping it opens a small menu:
 *   stage open   Lyrics · Video · Blank (what the screen shows), the song's
 *                saved videos to send, and **Close the stage window** (G3)
 *   no stage     **Open the stage window**, with the one line a teacher needs:
 *                drag it to the spare monitor once and leave it there
 *   iPad/phone   no window is offered (G4): one line says the stage runs on the
 *                room Mac, because a lesson on an iPad is a different browser
 *                and cannot reach the Mac's window.
 *
 * The menu is in the strip's own stacking context (z-index 3, above the
 * notes), anchored under the button, and closes on Escape or a tap outside.
 * Plain <button>s (a `wa-*` boolean prop can stick); each declares its height.
 *
 * A CHOICE THAT CANNOT TAKE SAYS WHY, IN WORDS (2026-09-28). Until then
 * "Lyrics" with no words loaded and "Video" with no video sent did nothing
 * at all, which read as broken buttons. Now an unavailable one is disabled
 * and a line under the row says what would make it work, and the song's
 * saved videos are one tap each from here rather than only from Videos.
 *
 * @param hasLyrics  the lesson's playing track has timed words
 * @param videos     the open song's saved videos, { lyrics, karaoke, performance }
 * @param songTitle  for the stage window's title when a video is sent
 */
const VIDEO_LABELS = { lyrics: 'Lyrics video', karaoke: 'Karaoke', performance: 'Performance' };

const StageButton = ({ hasLyrics = false, videos = null, songTitle = '' }) => {
	const { present, mode, has } = useStagePresence();
	const [open, setOpen] = useState(false);
	const [blocked, setBlocked] = useState(false);
	const wrapRef = useRef(null);
	const touch = isTouchDevice();

	useEffect(() => {
		if (!open) return undefined;
		const onDown = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
		const onKey = (e) => e.key === 'Escape' && setOpen(false);
		document.addEventListener('mousedown', onDown);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onDown);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const label = present ? `On · Stage window · ${stageModeLabel(mode)}` : 'Second screen · Off';

	// The lesson's own words count too: they reach the stage with the next
	// message, so "Lyrics" works the moment they exist on this side.
	const available = { lyrics: Boolean((has && has.lyrics) || hasLyrics), video: Boolean(has && has.video), blank: true };
	const saved = videos ? Object.keys(VIDEO_LABELS).filter((k) => videos[k]) : [];
	const sendVideo = (kind) => {
		const title = [songTitle, VIDEO_LABELS[kind]].filter(Boolean).join(' · ');
		const ok = openVideoWindow(videos[kind], title);
		setBlocked(!ok);
		if (ok) setOpen(false);
	};

	return (
		<div className="ld-stagebtn" ref={wrapRef}>
			<button
				type="button"
				className={`ld-stagebtn__btn${present ? ' is-on' : ''}`}
				aria-expanded={open}
				aria-haspopup="true"
				onClick={() => setOpen((v) => !v)}>
				<i className="fa-sharp fa-solid fa-display" aria-hidden="true" />
				{label}
			</button>

			{open && (
				<div className="ld-stagebtn__menu" role="group" aria-label="Second screen">
					{touch ? (
						<p className="ld-stagebtn__hint">
							The stage window runs on the room Mac. Teach from the Mac to show lyrics or videos on the spare monitor.
						</p>
					) : present ? (
						<>
							<p className="ld-stagebtn__hint">Show on the stage window:</p>
							<div className="ld-stagebtn__modes">
								{['lyrics', 'video', 'blank'].map((m) => (
									<button
										key={m}
										type="button"
										className={`ld-stagebtn__mode${mode === m ? ' is-on' : ''}`}
										aria-pressed={mode === m}
										disabled={!available[m]}
										onClick={() => available[m] && setStageMode(m)}>
										{stageModeLabel(m)}
									</button>
								))}
							</div>
							{!available.lyrics && <p className="ld-stagebtn__why">Lyrics: play a track that has timed words.</p>}
							{saved.length > 0 ? (
								<>
									<p className="ld-stagebtn__hint">Send a saved video:</p>
									<div className="ld-stagebtn__modes ld-stagebtn__modes--wrap">
										{saved.map((k) => (
											<button key={k} type="button" className="ld-stagebtn__mode" onClick={() => sendVideo(k)}>
												{VIDEO_LABELS[k] || k}
											</button>
										))}
									</div>
								</>
							) : (
								!available.video && <p className="ld-stagebtn__why">Video: no video saved for this song yet. Save one in the Song panel, under Videos.</p>
							)}
							<button
								type="button"
								className="ld-stagebtn__action"
								onClick={() => {
									closeStage();
									setOpen(false);
								}}>
								Close the stage window
							</button>
						</>
					) : (
						<>
							<button
								type="button"
								className="ld-stagebtn__action ld-stagebtn__action--primary"
								onClick={() => {
									const ok = openStageWindow();
									setBlocked(!ok);
									if (ok) setOpen(false);
								}}>
								Open the stage window
							</button>
							<p className="ld-stagebtn__hint">
								Drag it to the spare monitor once and leave it there. Every lesson on this Mac uses it.
							</p>
						</>
					)}
				</div>
			)}

			{blocked && (
				<div className="ld-stagebtn__error ld-dismissable-error" role="alert">
					<p>The stage window was blocked. Allow pop-ups for this site, then try again.</p>
					<button type="button" className="ld-dismiss" onClick={() => setBlocked(false)}>
						Dismiss
					</button>
				</div>
			)}
		</div>
	);
};

export default StageButton;
